import { useLayoutEffect, useRef, useState } from "react";
import { CaretDown, Funnel, ListDashes, Minus, Plus, SquaresFour } from "@phosphor-icons/react";
import { Eyebrow } from "@/components/site/Blocks";
import { Rule, SplitText } from "@/components/site/Motion";

// Text tabs / filters with a hairline marker that slides under the active one (Maiko).
export const LineTabs = ({ items, value, onChange, testIdPrefix, tablist = false, className = "" }) => {
  const wrap = useRef(null);
  const [mark, setMark] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = [...(wrap.current?.querySelectorAll("[data-tab]") || [])].find((b) => b.dataset.tab === String(value));
    if (!el) return undefined;
    const place = () => setMark({ left: el.offsetLeft, width: el.offsetWidth });
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [value, items.length]);
  return (
    <div ref={wrap} className={`s-filter-bar ${className}`} role={tablist ? "tablist" : undefined} data-testid={`${testIdPrefix}s`}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role={tablist ? "tab" : undefined}
          data-tab={t.id}
          aria-selected={tablist ? value === t.id : undefined}
          aria-pressed={tablist ? undefined : value === t.id}
          data-testid={t.testId || `${testIdPrefix}-${t.id}`}
          onClick={() => onChange(t.id)}
          className={`s-micro pb-3 !text-[12px] tracking-[0.12em] transition-colors duration-300 ${
            value === t.id ? "text-[color:var(--s-ink)]" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
          }`}
        >
          {t.label}
        </button>
      ))}
      <span className="s-filter-marker" style={{ left: mark.left, width: mark.width }} aria-hidden="true" />
    </div>
  );
};

// Maiko shop page title: hairline · eyebrow + airy headline · hairline.
export const ShopTitle = ({ eyebrow, title, body, right, testId, titleTestId, bodyTestId, compact = false }) => (
  <div data-testid={testId}>
    {!compact && <Rule className="is-in" />}
    <div className={`flex flex-col gap-8 ${compact ? "py-5 sm:py-7" : "py-12 sm:py-16"} lg:flex-row lg:items-end lg:justify-between`}>
      <div className="s-rise max-w-4xl">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className={`s-hx break-words ${compact ? "text-3xl sm:text-4xl lg:text-5xl" : "text-4xl sm:text-6xl lg:text-7xl"} ${eyebrow ? "mt-6" : ""}`} data-testid={titleTestId}>
          <SplitText text={title} />
        </h1>
        {body && (
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg" data-testid={bodyTestId}>
            {body}
          </p>
        )}
      </div>
      {right}
    </div>
    <Rule />
  </div>
);

// "Showing 1–12 of 40 results" · sort · columns · grid/list · filter drawer toggle
export const ShopToolbar = ({
  start, end, total, extra,
  sort, onSort, sortOptions,
  view, onView,
  cols, onCols, maxCols,
  panel, onPanel, activeCount,
  prefix, filterTestId = "filters-open", filterCountTestId = "filters-count", filterAlways = false,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[color:var(--s-line)] pb-5" data-testid={`${prefix}-toolbar`}>
    <div className="flex flex-wrap items-center gap-4">
      <p className="text-sm text-[color:var(--s-mute)]" data-testid={`${prefix}-count`}>
        {total === 0 ? "No results" : `Showing ${start}–${end} of ${total} ${total === 1 ? "result" : "results"}`}
      </p>
      {extra}
    </div>
    <div className="flex flex-wrap items-center gap-5">
      <button type="button" data-testid={filterTestId} onClick={onPanel} aria-expanded={panel} className={`s-micro inline-flex items-center gap-2 !text-[11px] text-[color:var(--s-ink)] ${filterAlways ? "" : "lg:hidden"}`}>
        <Funnel size={14} weight="bold" className="text-[color:var(--s-gold)]" /> {panel && !filterAlways ? "Hide filters" : "Filters"}
        {activeCount > 0 && (
          <span data-testid={filterCountTestId} className="flex h-5 min-w-5 items-center justify-center bg-[color:var(--s-gold-bright)] px-1.5 text-[10px] font-bold text-[color:var(--s-ink)]">
            {activeCount}
          </span>
        )}
      </button>
      <label className="relative inline-flex items-center">
        <select className="s-select" value={sort} onChange={(e) => onSort(e.target.value)} data-testid={`${prefix}-sort`} aria-label="Sort">
          {sortOptions.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <CaretDown size={12} weight="bold" className="pointer-events-none absolute right-1 text-[color:var(--s-mute)]" />
      </label>
      {onCols && view === "grid" && maxCols > 1 && (
        <div className="hidden items-center gap-1 sm:flex" data-testid={`${prefix}-cols-picker`} role="group" aria-label="Tiles per row">
          <button type="button" data-testid={`${prefix}-cols-minus`} aria-label="Fewer per row" disabled={cols <= 1} onClick={() => onCols(cols - 1)} className="flex h-9 w-9 items-center justify-center text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)] disabled:opacity-30">
            <Minus size={14} weight="bold" />
          </button>
          <span className="s-num min-w-[1.5rem] text-center text-sm text-[color:var(--s-ink)]" data-testid={`${prefix}-cols`} title="Tiles per row">{cols}</span>
          <button type="button" data-testid={`${prefix}-cols-plus`} aria-label="More per row" disabled={cols >= maxCols} onClick={() => onCols(cols + 1)} className="flex h-9 w-9 items-center justify-center text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)] disabled:opacity-30">
            <Plus size={14} weight="bold" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-1" data-testid={`${prefix}-view`}>
        {[["grid", SquaresFour, "Grid view"], ["list", ListDashes, "List view"]].map(([v, Icon, label]) => (
          <button
            key={v}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={view === v}
            data-testid={`${prefix}-view-${v}`}
            onClick={() => onView(v)}
            className={`flex h-9 w-9 items-center justify-center transition-colors duration-300 ${
              view === v ? "bg-[color:var(--s-ink)] text-white" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
            }`}
          >
            <Icon size={16} weight="bold" />
          </button>
        ))}
      </div>
    </div>
  </div>
);

export const Pager = ({ page, pages, onPage, prefix }) => {
  if (pages <= 1) return null;
  const nums = [...Array(pages)].map((_, i) => i + 1).filter((n) => n === 1 || n === pages || Math.abs(n - page) <= 1);
  const out = [];
  nums.forEach((n, i) => {
    if (i && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return (
    <nav className="mt-16 flex flex-wrap items-center justify-center gap-2" aria-label="Pages" data-testid={`${prefix}-pager`}>
      <button type="button" className="s-page-btn px-4" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${prefix}-prev`}>
        Prev
      </button>
      {out.map((n, i) =>
        n === "…" ? (
          <span key={`e${i}`} className="px-2 text-[color:var(--s-mute)]">…</span>
        ) : (
          <button
            key={n}
            type="button"
            className={`s-page-btn ${n === page ? "is-on" : ""}`}
            aria-current={n === page ? "page" : undefined}
            onClick={() => onPage(n)}
            data-testid={`${prefix}-page-${n}`}
          >
            {n}
          </button>
        ),
      )}
      <button type="button" className="s-page-btn px-4" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${prefix}-next`}>
        Next
      </button>
    </nav>
  );
};

export const scrollToTopOf = (el) => {
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
};
